import { Box, Text, Tooltip, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { statusColors } from "../../../constants/color-constants";
import {
  OPCD_API_STATUS,
  OPCD_STATUS_FILTER_OPTIONS,
} from "../../../constants/opcd-constants";
import { RouteConstants } from "../../../constants/route-constant";
import { useOpcdQueryList } from "../../../hooks/queries";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setOpcdQueriesCount } from "../../../store/providerReducer";
import type {
  OpcdQuery,
  OpcdQueryListRow,
} from "../../../types/opcd-queries";
import {
  filterOpcdQueriesBySearch,
  filterOpcdQueriesByStatus,
  isOpcdQueryPending,
  transformOpcdQueriesToRows,
} from "../../../utils/opcd-query-utils";
import FilterComponent from "../../Filter";
import PaginationComponent from "../../Shared/Pagination";
import { ReusableTable } from "../../Shared/table";

const limit = 15;

const OpcdQueries: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
  const providerNPI = useAppSelector((state) => state.provider.providerNPI);
  const opcdQueriesCount = useAppSelector(
    (state) => state.provider.opcdQueriesCount
  );
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string[]>([
    OPCD_API_STATUS.PENDING,
  ]);
  const [search, setSearch] = useState("");

  const { data, isLoading, isFetching } = useOpcdQueryList(providerNPI, {
    enabled: Boolean(providerNPI),
    polling: true,
  });

  const filteredRows = useMemo(() => {
    const rows = transformOpcdQueriesToRows(
      data ?? { success: true, count: 0, queries: [] }
    );
    return filterOpcdQueriesBySearch(
      filterOpcdQueriesByStatus(
        rows,
        statusFilter.length ? statusFilter : [OPCD_API_STATUS.PENDING]
      ),
      search
    );
  }, [data, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / limit));
  const paginatedData = useMemo(
    () => filteredRows.slice((page - 1) * limit, page * limit),
    [filteredRows, page]
  );

  useEffect(() => {
    if (!data) return;
    const pendingCount = data.queries.filter((query: OpcdQuery) =>
      isOpcdQueryPending(query.status)
    ).length;
    if (pendingCount !== opcdQueriesCount) {
      dispatch(setOpcdQueriesCount(pendingCount));
    }
  }, [data, dispatch, opcdQueriesCount]);

  useEffect(() => setPage(1), [search, statusFilter]);

  const columns = [
    {
      header: "Patient Name / MRN",
      align: "left" as const,
      accessor: (row: OpcdQueryListRow) => (
        <Tooltip
          label={
            <div>
              <div>{row.patientName}</div>
              <div>{row.patientMRN}</div>
            </div>
          }
          position="bottom-start"
          withArrow
          multiline
        >
          <div>
            <Text size={isSmallScreen ? "12px" : "14px"}>
              {row.patientName}
            </Text>
            <Text size="xs" c="dimmed" mt="2px">
              {row.patientMRN}
            </Text>
          </div>
        </Tooltip>
      ),
      width: "220px",
    },
    {
      header: "Status",
      align: "left" as const,
      accessor: "status" as keyof OpcdQueryListRow,
      width: "120px",
      cellType: "status" as const,
    },
    {
      header: "Ageing",
      align: "left" as const,
      accessor: "ageing" as keyof OpcdQueryListRow,
      width: "120px",
      cellType: "aging" as const,
    },
    {
      header: "Date",
      align: "left" as const,
      accessor: (row: OpcdQueryListRow) => (
        <Tooltip
          label={`${row.queryDate}, ${row.createdFormatTime}`}
          position="bottom-start"
          withArrow
        >
          <Text size={isSmallScreen ? "12px" : "14px"}>{row.queryDate}</Text>
        </Tooltip>
      ),
      width: "140px",
    },
    {
      header: "Query ID",
      align: "left" as const,
      accessor: "queryId" as keyof OpcdQueryListRow,
      width: "180px",
    },
  ];

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box style={{ flexShrink: 0 }}>
        <FilterComponent
          loading={isLoading && !data}
          refreshing={isFetching && !isLoading}
          dropDown1Options={OPCD_STATUS_FILTER_OPTIONS}
          dropDown2Options={[]}
          dropdown1Values={statusFilter}
          setDropdown1Values={setStatusFilter}
          dropdown2Values={[]}
          setDropdown2Values={() => undefined}
          hideSecondDropdown
          hideThirdDropdown
          searchValue={search}
          setSearchValue={setSearch}
          debounceDelay={500}
          dropdown1PlaceHolder="Select Status"
        />
      </Box>

      <ReusableTable
        loading={isLoading}
        data={paginatedData}
        columns={columns}
        getRowId={(row) => row.id}
        onRowClick={(row) =>
          navigate(
            RouteConstants.ROUTE_OPCD_QUERY_DETAILS.replace(
              ":query_id",
              row.queryId
            ),
            { state: { from: RouteConstants.ROUTE_OPCD_QUERIES } }
          )
        }
        statusColors={statusColors}
        showSeriesNumber
        showActionButtons={false}
      />

      <Box
        style={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          padding: "4px",
        }}
      >
        <PaginationComponent
          total={totalPages}
          value={page}
          onChange={setPage}
          disabled={isLoading}
        />
      </Box>
    </div>
  );
};

export default OpcdQueries;
